import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, BellIcon, CalendarIcon, CheckIcon, ShieldIcon, UsersIcon, ZapIcon } from 'lucide-react';
import { BrandIcon } from '../components/BrandIcon';
export const LandingPage = () => {
  return <div className="w-full min-h-screen bg-white text-gray-900" data-id="element-751">
      {/* Navigation */}
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex justify-between items-center" data-id="element-752">
        <div className="flex items-center" data-id="element-753">
          <BrandIcon width={40} height={40} data-id="element-754" />
          <span className="ml-3 text-2xl font-bold" data-id="element-755">LaVee.life</span>
        </div>
        <div className="hidden md:flex items-center space-x-8" data-id="element-756">
          <a href="#how-it-works" className="font-medium hover:text-sky-600 transition-colors" data-id="element-757">
            How It Works
          </a>
          <a href="#features" className="font-medium hover:text-sky-600 transition-colors" data-id="element-758">
            Features
          </a>
          <a href="#partners" className="font-medium hover:text-sky-600 transition-colors" data-id="element-759">
            Partners
          </a>
          <Link to="/onboard/setup" className="bg-gradient-to-r from-sky-500 to-blue-600 text-white px-6 py-2.5 rounded-lg font-medium hover:from-sky-600 hover:to-blue-700 transition-colors" data-id="element-760">
            Get Started
          </Link>
        </div>
        <button className="md:hidden" data-id="element-761">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-762">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" data-id="element-763" />
          </svg>
        </button>
      </nav>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-sky-500 to-blue-600 text-white py-20 overflow-hidden" data-id="element-764">
        <div className="absolute inset-0 opacity-10" data-id="element-765">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" data-id="element-766">
            <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse" data-id="element-767">
              <path d="M 10 0 L 0 0 0 10" fill="none" stroke="white" strokeWidth="0.5" data-id="element-768" />
            </pattern>
            <rect width="100" height="100" fill="url(#grid)" data-id="element-769" />
          </svg>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-770">
          <div className="flex flex-col md:flex-row items-center" data-id="element-771">
            <div className="md:w-1/2 mb-10 md:mb-0" data-id="element-772">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6" data-id="element-773">
                Get out. Do it with friends.
              </h1>
              <p className="text-xl md:text-2xl mb-8 max-w-xl" data-id="element-774">
                LaVee.life connects you to real-world events based on your
                interests, and your people. LaVee takes the pain out of discovering and planning, giving
                you more time to get out and live your best life.
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-775">
                <Link to="/onboard/setup" className="bg-white text-blue-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors flex items-center justify-center" data-id="element-776">
                  Sign Up for Early Access
                  <ArrowRightIcon className="ml-2" size={20} data-id="element-777" />
                </Link>
                <a href="#how-it-works" className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:bg-opacity-10 transition-colors flex items-center justify-center" data-id="element-778">
                  Learn More
                </a>
              </div>
            </div>
            <div className="md:w-1/2" data-id="element-779">
              <div className="relative rounded-xl overflow-hidden shadow-2xl" data-id="element-780">
                <img src="https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?q=80&w=1974&auto=format&fit=crop" alt="People enjoying a concert" className="w-full object-cover" data-id="element-781" />
                <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-60" data-id="element-782"></div>
                <div className="absolute bottom-0 left-0 p-6" data-id="element-783">
                  <div className="bg-white bg-opacity-90 rounded-lg p-4 max-w-xs" data-id="element-784">
                    <div className="flex items-center mb-2" data-id="element-785">
                      <CalendarIcon className="text-grey-600 mr-2" size={20} data-id="element-786" />
                      <span className="text-sky-600 font-bold" data-id="element-787">
                        Upcoming for You
                      </span>
                    </div>
                    <p className="text-sky-600 font-medium" data-id="element-788">
                      EDM Festival this weekend, 3 friends going
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-gray-50" data-id="element-789">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-790">
          <div className="text-center mb-16" data-id="element-791">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-792">
              How LaVee.life Works
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-793">
              Spend less time searching and more time living with our intuitive
              platform.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-794">
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow" data-id="element-795">
              <div className="w-14 h-14 bg-sky-100 rounded-full flex items-center justify-center mb-4" data-id="element-796">
                <ZapIcon className="text-sky-600" size={28} data-id="element-797" />
              </div>
              <h3 className="text-xl font-bold mb-2" data-id="element-798">Personalized Feed</h3>
              <p className="text-gray-600" data-id="element-799">
                Your interests, preferences, and location create a custom stream
                of events and experiences.
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow" data-id="element-800">
              <div className="w-14 h-14 bg-sky-100 rounded-full flex items-center justify-center mb-4" data-id="element-801">
                <BellIcon className="text-sky-600" size={28} data-id="element-802" />
              </div>
              <h3 className="text-xl font-bold mb-2" data-id="element-803">Smart Notifications</h3>
              <p className="text-gray-600" data-id="element-804">
                Get timely alerts about events you'll love, without the noise of
                things you don't.
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow" data-id="element-805">
              <div className="w-14 h-14 bg-sky-100 rounded-full flex items-center justify-center mb-4" data-id="element-806">
                <CalendarIcon className="text-sky-600" size={28} data-id="element-807" />
              </div>
              <h3 className="text-xl font-bold mb-2" data-id="element-808">Calendar Integration</h3>
              <p className="text-gray-600" data-id="element-809">
                Seamlessly add events to your calendar with all the details you
                need.
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow" data-id="element-810">
              <div className="w-14 h-14 bg-sky-100 rounded-full flex items-center justify-center mb-4" data-id="element-811">
                <UsersIcon className="text-sky-600" size={28} data-id="element-812" />
              </div>
              <h3 className="text-xl font-bold mb-2" data-id="element-813">Friend Collaboration</h3>
              <p className="text-gray-600" data-id="element-814">
                Plan, coordinate, and experience events together with built-in
                social tools.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Features Section */}
      <section id="features" className="py-20" data-id="element-815">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-816">
          <div className="text-center mb-16" data-id="element-817">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-818">
              Your Life, Perfectly Orchestrated
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-819">
              LaVee.life brings together everything you need to discover, plan,
              and experience the events that matter to you.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center mb-20" data-id="element-820">
            <div data-id="element-821">
              <h3 className="text-2xl font-bold mb-4" data-id="element-822">Hyper-Personalization</h3>
              <p className="text-lg text-gray-600 mb-6" data-id="element-823">
                Finally, an events platform that gets you. Our advanced
                algorithms learn your preferences over time to deliver
                increasingly relevant recommendations.
              </p>
              <ul className="space-y-3" data-id="element-824">
                {['Interest-based matching', 'Location-aware suggestions', 'Preference learning', 'Custom notification levels'].map((item, index) => <li key={index} className="flex items-start" data-id="element-825">
                    <CheckIcon className="text-sky-500 mr-2 mt-1 flex-shrink-0" size={20} data-id="element-826" />
                    <span data-id="element-827">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="rounded-xl overflow-hidden shadow-lg" data-id="element-828">
              <img src="https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?q=80&w=2070&auto=format&fit=crop" alt="Person using mobile app" className="w-full h-full object-cover" data-id="element-829" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center mb-20" data-id="element-830">
            <div className="order-2 md:order-1 rounded-xl overflow-hidden shadow-lg" data-id="element-831">
              <img src="https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?q=80&w=2070&auto=format&fit=crop" alt="Friends enjoying an event together" className="w-full h-full object-cover" data-id="element-832" />
            </div>
            <div className="order-1 md:order-2" data-id="element-833">
              <h3 className="text-2xl font-bold mb-4" data-id="element-834">
                Seamless Social Collaboration
              </h3>
              <p className="text-lg text-gray-600 mb-6" data-id="element-835">
                Experience it together. LaVee.life makes it easy to coordinate
                with friends and family for any event.
              </p>
              <ul className="space-y-3" data-id="element-836">
                {['Group planning tools', 'Shared calendars', 'Ticket purchasing coordination', 'In-app chat for event details'].map((item, index) => <li key={index} className="flex items-start" data-id="element-837">
                    <CheckIcon className="text-sky-500 mr-2 mt-1 flex-shrink-0" size={20} data-id="element-838" />
                    <span data-id="element-839">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center" data-id="element-840">
            <div data-id="element-841">
              <h3 className="text-2xl font-bold mb-4" data-id="element-842">
                Privacy-First Commitment
              </h3>
              <p className="text-lg text-gray-600 mb-6" data-id="element-843">
                Your life is yours alone. We believe in transparent data
                practices and putting you in control of your information.
              </p>
              <ul className="space-y-3" data-id="element-844">
                {['No data selling', 'Transparent privacy controls', 'Opt-in sharing only', 'Data export and deletion options'].map((item, index) => <li key={index} className="flex items-start" data-id="element-845">
                    <CheckIcon className="text-sky-500 mr-2 mt-1 flex-shrink-0" size={20} data-id="element-846" />
                    <span data-id="element-847">{item}</span>
                  </li>)}
              </ul>
              <div className="mt-6 flex items-center" data-id="element-848">
                <ShieldIcon className="text-sky-500 mr-2" size={24} data-id="element-849" />
                <span className="font-medium" data-id="element-850">
                  Your data never leaves our secure platform without your
                  explicit consent.
                </span>
              </div>
            </div>
            <div className="rounded-xl overflow-hidden shadow-lg bg-gray-50 p-8 flex items-center justify-center" data-id="element-851">
              <div className="text-center" data-id="element-852">
                <ShieldIcon className="text-sky-500 mx-auto mb-4" size={64} data-id="element-853" />
                <h4 className="text-xl font-bold mb-2" data-id="element-854">Privacy Guarantee</h4>
                <p className="text-gray-600" data-id="element-855">
                  We will never sell your personal data to third parties or use
                  it for purposes you haven't explicitly approved.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* User Personas Section */}
      <section className="py-20 bg-gray-900 text-white" data-id="element-856">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-857">
          <div className="text-center mb-16" data-id="element-858">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-859">
              For Every Fan & Interest Level
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto" data-id="element-860">
              Whether you're a die-hard fan, social planner, or just curious
              about what's happening, LaVee.life adapts to your style.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-861">
            <div className="bg-gray-800 p-8 rounded-xl" data-id="element-862">
              <div className="w-16 h-16 bg-sky-800 rounded-full flex items-center justify-center mb-6" data-id="element-863">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-sky-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-864">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" data-id="element-865" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-3" data-id="element-866">The Die-Hard Fan</h3>
              <p className="text-gray-300 mb-6" data-id="element-867">
                For the passionate enthusiast who never wants to miss a beat
                about their favorite teams, artists, or events.
              </p>
              <ul className="space-y-3" data-id="element-868">
                {['Real-time updates', 'Insider news access', 'Stats and analytics', 'Community features'].map((item, index) => <li key={index} className="flex items-start" data-id="element-869">
                    <CheckIcon className="text-sky-400 mr-2 mt-1 flex-shrink-0" size={20} data-id="element-870" />
                    <span data-id="element-871">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="bg-gray-800 p-8 rounded-xl" data-id="element-872">
              <div className="w-16 h-16 bg-sky-800 rounded-full flex items-center justify-center mb-6" data-id="element-873">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-sky-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-874">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20h2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" data-id="element-875" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-3" data-id="element-876">The Social Planner</h3>
              <p className="text-gray-300 mb-6" data-id="element-877">
                For those who love organizing group outings and making memories
                with friends at events.
              </p>
              <ul className="space-y-3" data-id="element-878">
                {['Group coordination tools', 'Shared ticketing', 'Event polling', 'Expense splitting'].map((item, index) => <li key={index} className="flex items-start" data-id="element-879">
                    <CheckIcon className="text-sky-400 mr-2 mt-1 flex-shrink-0" size={20} data-id="element-880" />
                    <span data-id="element-881">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="bg-gray-800 p-8 rounded-xl" data-id="element-882">
              <div className="w-16 h-16 bg-sky-800 rounded-full flex items-center justify-center mb-6" data-id="element-883">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-sky-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-884">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-885" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-3" data-id="element-886">The Curious Explorer</h3>
              <p className="text-gray-300 mb-6" data-id="element-887">
                For those who want to discover new experiences and expand their
                horizons with minimal effort.
              </p>
              <ul className="space-y-3" data-id="element-888">
                {['Curated discoveries', 'Low-commitment options', 'Last-minute availability', 'Trend spotting'].map((item, index) => <li key={index} className="flex items-start" data-id="element-889">
                    <CheckIcon className="text-sky-400 mr-2 mt-1 flex-shrink-0" size={20} data-id="element-890" />
                    <span data-id="element-891">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>
      {/* Partners Section */}
      <section id="partners" className="py-20 bg-white" data-id="element-892">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-893">
          <div className="text-center mb-16" data-id="element-894">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-895">
              In Partnership With The Best
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-896">
              We partner with the world's leading content producers to bring you
              closer to the action and stories you love.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 items-center" data-id="element-897">
            {/* These would be actual partner logos in a real implementation */}
            {[1, 2, 3, 4, 5, 6].map(index => <div key={index} className="bg-gray-100 p-6 rounded-lg flex items-center justify-center h-24" data-id="element-898">
                <div className="text-gray-400 font-bold" data-id="element-899">Partner Logo</div>
              </div>)}
          </div>
        </div>
      </section>
      {/* Social Proof Section (Future) */}
      <section className="py-20 bg-gray-50" data-id="element-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-901">
          <div className="text-center mb-16" data-id="element-902">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-903">
              What Our Users Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-904">
              Join thousands of satisfied users who have transformed how they
              discover and experience events.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-905">
            {/* Placeholder for future testimonials */}
            {[1, 2, 3].map(index => <div key={index} className="bg-white p-8 rounded-xl shadow-md" data-id="element-906">
                <div className="flex items-center mb-4" data-id="element-907">
                  <div className="w-12 h-12 bg-gray-200 rounded-full mr-4" data-id="element-908"></div>
                  <div data-id="element-909">
                    <h4 className="font-bold" data-id="element-910">Future User</h4>
                    <p className="text-gray-500 text-sm" data-id="element-911">Die-Hard Sports Fan</p>
                  </div>
                </div>
                <p className="text-gray-600 italic" data-id="element-912">
                  "LaVee.life has completely changed how I experience my
                  favorite sports. I never miss a game or important update now!"
                </p>
              </div>)}
          </div>
        </div>
      </section>
      {/* Final CTA Section */}
      <section className="py-20 bg-sky-600 text-white" data-id="element-913">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-914">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-915">
            Ready to Orchestrate Your Perfect Life?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto" data-id="element-916">
            Join LaVee.life today and start discovering events that match your
            interests, location, and desired level of engagement.
          </p>
          <Link to="/onboard/setup" className="inline-block bg-white text-sky-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors" data-id="element-917">
            Sign Up for Early Access
          </Link>
          <p className="mt-6 text-sky-100" data-id="element-918">
            No credit card required. Start for free.
          </p>
        </div>
      </section>
      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12" data-id="element-919">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-920">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8" data-id="element-921">
            <div data-id="element-922">
              <div className="flex items-center mb-4" data-id="element-923">
                <BrandIcon width={32} height={32} data-id="element-924" />
                <span className="ml-2 text-xl font-bold" data-id="element-925">LaVee.life</span>
              </div>
              <p className="text-gray-400" data-id="element-926">
                Your life, perfectly orchestrated.
              </p>
            </div>
            <div data-id="element-927">
              <h4 className="text-lg font-bold mb-4" data-id="element-928">Company</h4>
              <ul className="space-y-2" data-id="element-929">
                {['About Us', 'Careers', 'Blog', 'Press'].map((item, index) => <li key={index} data-id="element-930">
                    <a href="#" className="text-gray-400 hover:text-white transition-colors" data-id="element-931">
                      {item}
                    </a>
                  </li>)}
              </ul>
            </div>
            <div data-id="element-932">
              <h4 className="text-lg font-bold mb-4" data-id="element-933">Resources</h4>
              <ul className="space-y-2" data-id="element-934">
                {['Help Center', 'Community', 'Partners', 'Developers'].map((item, index) => <li key={index} data-id="element-935">
                      <a href="#" className="text-gray-400 hover:text-white transition-colors" data-id="element-936">
                        {item}
                      </a>
                    </li>)}
              </ul>
            </div>
            <div data-id="element-937">
              <h4 className="text-lg font-bold mb-4" data-id="element-938">Legal</h4>
              <ul className="space-y-2" data-id="element-939">
                {['Privacy Policy', 'Terms of Service', 'Cookie Policy', 'GDPR'].map((item, index) => <li key={index} data-id="element-940">
                    <a href="#" className="text-gray-400 hover:text-white transition-colors" data-id="element-941">
                      {item}
                    </a>
                  </li>)}
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center" data-id="element-942">
            <p className="text-gray-400 mb-4 md:mb-0" data-id="element-943">
              &copy; 2025 LaVee, Inc. All rights reserved.
            </p>
            <div className="flex space-x-6" data-id="element-944">
              {['Facebook', 'Twitter', 'Instagram', 'LinkedIn'].map((platform, index) => <a key={index} href="#" className="text-gray-400 hover:text-white transition-colors" data-id="element-945">
                    {platform}
                  </a>)}
            </div>
          </div>
        </div>
      </footer>
    </div>;
};